import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { changePasswordSchema, type ChangePasswordInput } from '@daater/shared';
import { Button } from '@daater/ui';
import { useChangePasswordMutation } from '../api/queries';
import { fetchCurrentAccount, logoutRequest } from '../api/endpoints';
import {
  buildPaymentRedirectSearch,
  resolveCurrentAccountAccess,
} from '../api/current-account-access';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import { useAuthStore } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';

/**
 * Cambio obligatorio para quien entra con la contraseña que traía del sistema
 * anterior. Hasta que la cambie, el API no le responde ninguna otra ruta, así
 * que esta pantalla no consulta nada más ni deja salir a otra parte.
 */
export default function ChangePasswordScreen() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const setAuth = useAuthStore(state => state.setAuth);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const setAccount = useAccountStore(state => state.setAccount);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const changePasswordMutation = useChangePasswordMutation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (values: ChangePasswordInput) => {
    setErrorMessage(null);
    try {
      const { token } = await changePasswordMutation.mutateAsync({
        newPassword: values.password,
      });

      // El token viejo queda invalidado por el cambio: hay que guardar el
      // nuevo antes de cualquier otra llamada.
      if (user) setAuth(user, token);

      const access = await resolveCurrentAccountAccess(fetchCurrentAccount);
      if (access.account) setAccount(access.account);

      if (access.decision === 'payment') {
        void navigate({
          to: '/payment',
          replace: true,
          search: buildPaymentRedirectSearch('', access.amount),
        });
        return;
      }

      void navigate({ to: '/', replace: true });
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const salir = () => {
    void logoutRequest().then(() => {
      clearAuth();
      clearAccount();
      void navigate({ to: '/login', replace: true });
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-pale-grey px-4">
      <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
        <DaaterLogoHomeLink />
        <h1 className="mt-4 text-2xl font-semibold text-charcoal-grey">Crea tu nueva contraseña</h1>
        <p className="mt-1 text-sm text-slate">
          Tu cuenta viene de la versión anterior de Daater. Por seguridad, define una contraseña
          nueva para entrar.
        </p>

        <form className="mt-6 space-y-4" onSubmit={event => void handleSubmit(onSubmit)(event)}>
          <div>
            <label className="text-xs font-semibold uppercase text-slate">Nueva contraseña</label>
            <input
              type="password"
              autoComplete="new-password"
              {...register('password')}
              className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
              placeholder="••••••••"
            />
            {errors.password ? (
              <p className="mt-1 text-xs text-darkish-pink">{errors.password.message}</p>
            ) : null}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-slate">
              Confirmar contraseña
            </label>
            <input
              type="password"
              autoComplete="new-password"
              {...register('repassword')}
              className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
              placeholder="••••••••"
            />
            {errors.repassword ? (
              <p className="mt-1 text-xs text-darkish-pink">{errors.repassword.message}</p>
            ) : null}
          </div>

          <Button type="submit" className="w-full" disabled={changePasswordMutation.isPending}>
            {changePasswordMutation.isPending ? 'Guardando...' : 'Guardar y continuar'}
          </Button>
          {errorMessage ? <p className="text-xs text-darkish-pink">{errorMessage}</p> : null}
        </form>

        <button type="button" onClick={salir} className="mt-4 text-xs text-slate underline">
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
